import { Gift, FlaskConical, LineChart, FileText, Wrench, Lock, LockOpen } from "lucide-react";
import { QUESTIONS, THC } from "@/lib/tankHealthCheck";

export function QualificationForm({ answers, onAnswer, ownerBlocked, sourceBlocked,
  onChangeAnswer }) {
  return (
    <div data-testid="thc-qualification">
      <h2 className="font-extrabold uppercase leading-[1.05] text-[29px] md:text-[36px] xl:text-[41px]"
        style={{ color: THC.navy, fontFamily: "'Outfit',system-ui,sans-serif" }}>
        A FEW QUICK QUESTIONS BEFORE YOU BOOK
      </h2>
      <p className="mt-1.5 text-[16px] xl:text-[17px]" style={{ color: THC.blue }}>
        Help us tailor your Health Check and confirm your eligibility — the complimentary
        Health Check is for properties on a private water supply (rainwater and/or bore water).
      </p>

      <div className="mt-5 space-y-[9px]">
        {QUESTIONS.map((q, i) => (
          <fieldset key={q.field} data-testid={`thc-q-${q.field}`}
            className="rounded-[8px] border p-3 md:px-4 md:py-3 flex flex-col md:flex-row md:items-center gap-2 md:gap-4 md:min-h-[60px]"
            style={{ borderColor: THC.border, backgroundColor: "#FFFFFF" }}>
            <legend className="sr-only">{q.label}</legend>
            <p className="text-[15px] md:text-[16px] font-semibold md:flex-1" style={{ color: THC.body }}>
              <span className="font-bold mr-1.5" style={{ color: THC.blue }}>{i + 1}.</span>{q.label}
            </p>
            <div className="flex flex-wrap gap-2 md:justify-end">
              {q.options.map(([value, label]) => {
                const active = answers[q.field] === value;
                return (
                  <label key={value} data-testid={`thc-opt-${q.field}-${value}`}
                    className="cursor-pointer select-none inline-flex items-center justify-center min-h-[44px] px-3 rounded-[8px] border text-[14.5px] font-semibold transition-colors focus-within:ring-2 focus-within:ring-[#09A9E6]"
                    style={{
                      borderColor: active ? THC.blue : THC.border,
                      backgroundColor: active ? THC.blue : THC.faint,
                      color: active ? "#FFFFFF" : THC.body,
                    }}>
                    <input type="radio" name={q.field} value={value} checked={active}
                      onChange={() => onAnswer(q.field, value)} className="sr-only" />
                    {label}
                  </label>
                );
              })}
            </div>
          </fieldset>
        ))}
      </div>

      {sourceBlocked ? (
        <div className="mt-4 rounded-[13px] border-2 p-4 md:p-5" data-testid="thc-source-required"
          style={{ borderColor: THC.red, backgroundColor: "#FEF0EF" }}>
          <h3 className="font-extrabold uppercase text-[19px] md:text-[22px]" style={{ color: THC.red }}>
            PRIVATE WATER SUPPLY REQUIRED
          </h3>
          <p className="mt-2 text-[15.5px] md:text-[16px] leading-[1.5]" style={{ color: THC.body }}>
            The complimentary Tank Water Health Check is for properties that rely on rainwater
            and/or bore water. Town-water-only properties are not eligible, so booking times
            stay locked.
          </p>
          <p className="mt-2 text-[15.5px] md:text-[16px] font-semibold" style={{ color: THC.body }}>
            If your property does use a rainwater tank or a bore, please update question 1.
          </p>
          <button onClick={() => onChangeAnswer("water_source")} data-testid="thc-change-source-answer"
            className="mt-3 h-[48px] px-5 rounded-[8px] text-white text-[16px] font-bold"
            style={{ backgroundColor: THC.red }}>
            CHANGE MY ANSWER
          </button>
        </div>
      ) : ownerBlocked ? (
        <div className="mt-4 rounded-[13px] border-2 p-4 md:p-5" data-testid="thc-owner-required"
          style={{ borderColor: THC.yellow, backgroundColor: THC.yellowSoft }}>
          <h3 className="font-extrabold uppercase text-[19px] md:text-[22px]" style={{ color: THC.yellowInk }}>
            OWNER ATTENDANCE REQUIRED
          </h3>
          <p className="mt-2 text-[15.5px] md:text-[16px] leading-[1.5]" style={{ color: THC.body }}>
            The value of the Tank Water Health Check is being able to see the findings and discuss
            them directly with the technician.
          </p>
          <p className="mt-2 text-[15.5px] md:text-[16px] font-semibold" style={{ color: THC.body }}>
            Please choose a time when the property owner can be present.
          </p>
          <button onClick={() => onChangeAnswer("owner_present")} data-testid="thc-change-answer"
            className="mt-3 h-[48px] px-5 rounded-[8px] text-[16px] font-bold"
            style={{ backgroundColor: THC.yellow, color: THC.navy }}>
            CHANGE MY ANSWER
          </button>
        </div>
      ) : (
        <p className="mt-4 inline-flex items-center gap-2 rounded-[10px] px-3 py-2 text-[14px] font-semibold"
          data-testid="thc-booking-lock-state"
          style={{ backgroundColor: answers.owner_present === "yes" ? THC.light : THC.faint, color: THC.navy }}>
          {answers.owner_present === "yes"
            ? (<><LockOpen className="w-4 h-4" aria-hidden="true" /> Booking times unlocked</>)
            : (<><Lock className="w-4 h-4" aria-hidden="true" /> Booking times will unlock once “Yes” is selected.</>)}
        </p>
      )}
    </div>
  );
}

export function BonusPanel() {
  /* Sep 2026 — no quantity limit: every valid campaign booking earns the
     20% lab bonus, so the scarcity counter is gone. */
  return (
    <div id="water-analysis-test" className="rounded-[14px] p-[22px] md:p-6 flex items-start gap-4" data-testid="thc-bonus-panel"
      style={{ backgroundColor: THC.green, boxShadow: "0 6px 20px rgba(15,138,76,0.25)" }}>
      <Gift className="w-[38px] h-[38px] md:w-[44px] md:h-[44px] text-white shrink-0" aria-hidden="true" />
      <div>
        <div className="text-white font-black leading-none text-[26px] md:text-[32px]"
          style={{ fontFamily: "'Outfit',system-ui,sans-serif" }}>CAMPAIGN BONUS</div>
        <p className="mt-1.5 text-white text-[15px] md:text-[16px] font-semibold leading-[1.35]">
          Book your Complimentary Tank Water Health Check through this campaign and receive
        </p>
        <div className="text-white font-black leading-none text-[38px] md:text-[46px]"
          style={{ fontFamily: "'Outfit',system-ui,sans-serif" }}>20% OFF</div>
        <p className="mt-1 text-white text-[15px] md:text-[16px] font-semibold leading-[1.35]">
          any AWARE laboratory water-test package.
        </p>
        <p className="mt-1.5 text-white/90 text-[13.5px] md:text-[14px] font-semibold">
          Available only through this campaign.
        </p>
      </div>
    </div>
  );
}

const INCLUDES = [
  [FlaskConical, "LABORATORY TESTING", "Scientific laboratory analysis"],
  [LineChart, "SCIENTIFIC RESULTS", "Clear measured data"],
  [FileText, "EASY-TO-UNDERSTAND SUMMARY", "Plain-English explanation of your results"],
  [Wrench, "RECOMMENDATIONS IF WATER CORRECTION IS NEEDED", "Practical next steps based on the findings"],
];

export function IncludesPanel() {
  return (
    <div className="relative rounded-[14px] border p-[18px] md:p-6 overflow-hidden" data-testid="thc-includes-panel"
      style={{ borderColor: THC.border, backgroundColor: THC.faint, boxShadow: THC.shadow }}>
      <h3 className="font-extrabold uppercase text-[21px] md:text-[24px]" style={{ color: THC.navy }}>
        EVERY AWARE TEST INCLUDES:
      </h3>
      <ul className="mt-4 space-y-3.5 md:pr-24">
        {INCLUDES.map(([Icon, title, copy]) => (
          <li key={title} className="flex items-start gap-3">
            <span className="shrink-0 w-[42px] h-[42px] rounded-full grid place-items-center"
              style={{ backgroundColor: THC.blue }}>
              <Icon className="w-5 h-5 text-white" aria-hidden="true" />
            </span>
            <div>
              <div className="font-bold text-[15px] md:text-[16px] leading-tight" style={{ color: THC.navy }}>{title}</div>
              <div className="text-[14px] md:text-[15px]" style={{ color: THC.muted }}>{copy}</div>
            </div>
          </li>
        ))}
      </ul>
      <p className="hidden md:block absolute right-4 bottom-5 text-[26px] leading-[1.05] text-right font-semibold"
        style={{ color: THC.bright, fontFamily: "'Caveat',cursive" }}>
        Know<br />What’s<br />in Your<br />Water
      </p>
      <p className="md:hidden mt-3 text-[24px] font-semibold"
        style={{ color: THC.bright, fontFamily: "'Caveat',cursive" }}>
        Know What’s In Your Water
      </p>
    </div>
  );
}
